import PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
// Component
import { EmptyText } from '../Common/CellText'
import NormalTag from '../NormalTag/NormalTag'
// Styled component
import {
  PopoverItem,
  PopoverList,
  PopoverTitle,
  TagWrapper,
  ViewLogModal,
} from './PopoverWatchTimeStyled'
// Ant design
import {
  BankOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HistoryOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Popover } from 'antd'
// Other
import { inject, observer } from 'mobx-react'
import { useState } from 'react'
import { v4 } from 'uuid'
import { blue, green, yellowPrimary } from '../../color'
import { ASSIGNEE_TYPE } from '../../constants'
import date_format from '../../date_format'
import useOpenItem from '../../hooks/useOpenItem'
import utils from '../../utils'
import SpinnerInlineComponent from '../SpinnerInlineComponent'

const findAssigneeType = assigneeType => {
  switch (assigneeType) {
    case ASSIGNEE_TYPE.USER:
      return <UserOutlined style={{ color: blue, marginRight: 6 }} />
    case ASSIGNEE_TYPE.DEPARTMENT:
      return <BankOutlined style={{ color: green, marginRight: 6 }} />
    case ASSIGNEE_TYPE.GROUP:
      return <TeamOutlined style={{ color: yellowPrimary, marginRight: 6 }} />
    default:
      return <UserOutlined style={{ color: blue, marginRight: 6 }} />
  }
}
const findTextColor = assignee_type => {
  return assignee_type === ASSIGNEE_TYPE.USER
    ? blue
    : assignee_type === ASSIGNEE_TYPE.DEPARTMENT
    ? green
    : yellowPrimary
}

const areSameUsers = (prevUsers, nextUsers) => {
  if (prevUsers.length !== nextUsers.length) {
    return false
  }

  return prevUsers.every(
    (user, index) =>
      user?.username === nextUsers[index]?.username &&
      user?.id === nextUsers[index]?.id &&
      user?.assignee_type === nextUsers[index]?.assignee_type
  )
}

const PopoverWatchTime = props => {
  const {
    userList,
    title,
    showFullTag,
    internalDocumentStore,
    selectPeopleStore,
    isFullViewer,
    typeId,
    compared_date,
    created_at = '',
    departWithUsers,
    verticalTag,
    resourceType = 'document',
    fetchViewLogs,
  } = props

  const { viewLogList, setViewLogList, getViewLog } = internalDocumentStore
  const {
    isOpen: isVisibleViewLog,
    handleOpen: handelVisibleLogView,
    handleClose: handleCancelLogView,
  } = useOpenItem()
  const [isOpenPopoverList, setIsOpenPopoverList] = useState(false)
  const [users, setUsers] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const handlePopoverList = async value => {
    setIsOpenPopoverList(value)
    if (value && resourceType === 'news') {
      await handleGetViewLog([])
    }
  }
  // userList có dạng:
  // userList = [
  //   {
  //     assignee_type: "USER"
  //     full_name: "ĐỖ ĐỨC ÂN"
  //     id: "andd"
  // last_date_read
  //     permission: "LEAD"
  //   }
  //   ...
  // ]
  const createdAt = new Date(created_at)
  const comparedDate = new Date(compared_date)

  const groups = useMemo(
    () =>
      (userList || []).filter(
        item => item.assignee_type !== ASSIGNEE_TYPE.USER
      ),
    [userList]
  )

  const usersPre = useMemo(
    () =>
      (userList || []).filter(
        item => item.assignee_type === ASSIGNEE_TYPE.USER
      ),
    [userList]
  )

  useEffect(() => {
    if (resourceType === 'news') {
      setUsers([])
      return
    }

    const usersNotYet = usersPre.filter(
      item =>
        !(
          (viewLogList || []).filter(
            log =>
              (log.username === item.username || log.username === item.id) &&
              item.assignee_type !== ASSIGNEE_TYPE.USER
          )?.length > 0
        )
    )

    setUsers(prevUsers =>
      areSameUsers(prevUsers, usersNotYet) ? prevUsers : usersNotYet
    )
  }, [viewLogList, resourceType, usersPre])

  const handleGetViewLog = async groups => {
    if (resourceType === 'news' && fetchViewLogs) {
      setIsLoading(true)
      try {
        const response = await fetchViewLogs(typeId)
        const normalizedLogs = (response?.data || []).map(item => ({
          ...item,
          username: item.username || item.user_name,
          full_name:
            item.fullName || item.full_name || item.user_name || item.username,
          assignee_type: ASSIGNEE_TYPE.USER,
          first_date_read: item.firstDateRead || item.first_date_read,
          last_date_read:
            item.lastDateRead ||
            item.last_date_read ||
            item.firstDateRead ||
            item.first_date_read,
        }))
        setViewLogList(normalizedLogs)
      } finally {
        setIsLoading(false)
      }
      return
    }

    setIsLoading(true)
    await Promise.all([
      ...groups.map(group =>
        getViewLog({
          assignee_code: group.id,
          assignee_type: group.assignee_type,
          document_code: typeId,
        })
      ),

      getViewLog({
        assignee_code: usersPre.map(user => user.id).join(','),
        assignee_type: ASSIGNEE_TYPE.USER,
        document_code: typeId,
      }),
    ]).finally(() => setIsLoading(false))
  }

  const popoverList = (listSelected, selectGroup) => {
    let key = v4()
    return (
      <PopoverList key={key}>
        <div className={'popover-head'}>
          {selectGroup ? (
            <span
              style={{
                color: findTextColor(selectGroup.assignee_type),
                textTransform: 'capitalize',
                marginLeft: selectGroup ? '8px' : 0,
                maxWidth: 300,
              }}>
              {findAssigneeType(selectGroup.assignee_type)}
              {(selectGroup?.full_name ?? selectGroup?.name)?.toLowerCase()}
            </span>
          ) : (
            <b>
              <UserOutlined style={{ color: blue, marginRight: 6 }} />
              Người dùng
            </b>
          )}

          <b style={{ display: 'inline-block', width: 210 }}>
            <HistoryOutlined style={{ color: blue, marginRight: 6 }} />
            Thời gian xem
          </b>
        </div>

        {listSelected?.length > 0 ? (
          listSelected.map((el, index) => {
            let type = findAssigneeType(el?.assignee_type)
            let name
            let userInfo = utils.getInfoUser(
              el?.username,
              departWithUsers
                ? departWithUsers
                : selectPeopleStore.originDepartmentWithUserList
            )

            let department =
              userInfo?.full_name?.toLowerCase() ||
              el?.full_name?.toLowerCase() ||
              el?.fullName?.toLowerCase() ||
              el?.name

            if (department) {
              name = department
            } else {
              let users = userList?.filter(
                user =>
                  user.id === el?.username || user.username === el?.username
              )
              if (users && users.length > 0) {
                name = users[0]?.full_name
                  ?.toLowerCase()
                  ?.replace(/(^|\s)\S/g, l => l?.toUpperCase())
              }
            }

            // if (name) {
            return (
              <PopoverItem key={index}>
                <span
                  style={{
                    color: blue,
                    textTransform: 'capitalize',
                    marginLeft: selectGroup ? '8px' : 0,
                  }}>
                  {type}
                  {name}
                </span>
                <time>
                  {isFullViewer && createdAt < comparedDate ? (
                    <span>
                      <EyeOutlined style={{ color: green, marginRight: 6 }} />
                      Đã xem
                    </span>
                  ) : el.last_date_read || el.first_date_read ? (
                    <span>
                      <EyeOutlined style={{ color: green, marginRight: 6 }} />
                      {date_format.HH_mm_DD_MM_YYYY(
                        el.last_date_read || el.first_date_read
                      )}
                    </span>
                  ) : (
                    <EmptyText>
                      <EyeInvisibleOutlined
                        style={{ color: '#ccc', marginRight: 6 }}
                      />
                      Chưa xem.
                    </EmptyText>
                  )}
                </time>
              </PopoverItem>
            )
            // } else {
            //   return <></>
            // }
          })
        ) : (
          <EmptyText>
            <span style={{ marginLeft: 12 }}>Không có.</span>
          </EmptyText>
        )}
        {isLoading && (
          <div style={{ height: 150, position: 'relative' }}>
            <SpinnerInlineComponent
              sizeSpin={'small'}
              tip="Đang tải dữ liệu..."
              isLoading={isLoading}
              backGround={'#ffffffc7'}
            />
          </div>
        )}
      </PopoverList>
    )
  }
  const displayTags =
    userList?.length > 0
      ? userList.map(user => (user?.full_name ?? user?.name)?.toLowerCase())
      : [title || 'Đã xem']

  const isModalMode = resourceType === 'news' || isFullViewer

  return (
    <>
      {isModalMode ? (
        <TagWrapper
          onClick={() => {
            handleGetViewLog(groups)
            handelVisibleLogView()
          }}>
          <NormalTag
            noHover={true}
            showFullTag={showFullTag}
            vertical={verticalTag}
            tags={displayTags}
          />
        </TagWrapper>
      ) : (
        <Popover
          placement="top"
          content={() => popoverList(userList)}
          title={<PopoverTitle>{title}</PopoverTitle>}
          visible={isOpenPopoverList}
          onVisibleChange={handlePopoverList}
          trigger="click"
          overlayClassName={'wrapper-list'}
          overlayStyle={{ maxWidth: '450px' }}>
          <TagWrapper>
            <NormalTag
              noHover={true}
              showFullTag={showFullTag}
              vertical={verticalTag}
              tags={displayTags}
            />
          </TagWrapper>
        </Popover>
      )}

      <ViewLogModal
        title={'Danh sách người xem'}
        width={650}
        style={{ top: 40 }}
        visible={isVisibleViewLog}
        closable={true}
        maskClosable={true}
        onCancel={() => {
          handleCancelLogView()
          setViewLogList([])
        }}
        footer={null}
        wrapClassName="custom-modal-wrapper">
        <div style={{ padding: '12px' }}>
          {resourceType === 'news' ? popoverList(viewLogList) : null}
          {resourceType !== 'news' &&
            groups.map(group =>
              popoverList(
                viewLogList.filter(item => item.groupId === group.id),
                group
              )
            )}
          {resourceType !== 'news' && users?.length > 0
            ? popoverList(
                viewLogList.filter(
                  user => user.groupType === ASSIGNEE_TYPE.USER
                )
              )
            : ''}
        </div>
      </ViewLogModal>
    </>
  )
}

PopoverWatchTime.propTypes = {
  userList: PropTypes.array,
  title: PropTypes.string,
  showFullTag: PropTypes.bool,
  verticalTag: PropTypes.bool,
}

export default inject(
  'internalDocumentStore',
  'selectPeopleStore',
  'userGroupStore'
)(observer(PopoverWatchTime))
